import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { departmentAPI } from '@/services/api';
import { toast } from 'sonner';

// ─── Queries ────────────────────────────────────────────────────────────────

export const useDepartments = (params) =>
  useQuery({
    queryKey: ['departments', params],
    queryFn: async () => {
      const res = await departmentAPI.getAll(params);
      return res.data;
    },
    keepPreviousData: true,
  });

export const useDepartment = (id) =>
  useQuery({
    queryKey: ['department', id],
    queryFn: async () => {
      const res = await departmentAPI.getOne(id);
      return res.data;
    },
    enabled: !!id,
  });

// params included in key so pagination changes trigger a refetch
export const useDepartmentActivity = (id, params) =>
  useQuery({
    queryKey: ['department-activity', id, params],
    queryFn: async () => {
      const res = await departmentAPI.getActivity(id, params);
      return res.data;
    },
    enabled: !!id,
  });

// ─── Mutations ───────────────────────────────────────────────────────────────

export const useCreateDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => departmentAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department created successfully');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to create department');
    },
  });
};

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => departmentAPI.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['department', id] });
      toast.success('Department updated successfully');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to update department');
    },
  });
};

export const useArchiveDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => departmentAPI.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department archived');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to archive department');
    },
  });
};

export const useRestoreDepartment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => departmentAPI.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department restored');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to restore department');
    },
  });
};

export const useAddMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, userId }) => departmentAPI.addMember(id, userId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['department', id] });
      toast.success('Member added');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to add member');
    },
  });
};

export const useRemoveMember = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, memberId }) => departmentAPI.removeMember(id, memberId),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['department', id] });
      toast.success('Member removed');
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Failed to remove member');
    },
  });
};
